/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-expressions, @typescript-eslint/no-this-alias, @typescript-eslint/no-unused-vars, @typescript-eslint/no-namespace */
import L from "leaflet";
import SVGInject from "@iconfu/svg-inject";
import $ from "jquery";
import configurationUtils from "../../../../utils/IconConfiguration";
import { colorDefault } from "@/utils/ColorDefault";
import { MARKER_HOVER_DELAY_MS } from "../markerHoverDelay";

interface SvgIconOptions extends L.IconOptions {
  id?: string;
  state?: string;
  activityState?: string;
  network?: boolean;
  decoratorColor?: string | null;
  alertSeverity?: string | null;
  type?: string;
  fixedStyle?: {
    bgColor: string;
    iconColor: string;
  } | null;
  containerEl?: string | null;
  trackerName?: string;
  // When false the marker is fully static: no hover style/popup and no
  // click-to-select (no orange style, no selected-marker event). Defaults to true.
  selectable?: boolean;
}

interface FixedStyle {
  bgColor: string;
  iconColor: string;
}

declare module "leaflet" {
  namespace L {
    class SvgIcon {
      options: SvgIconOptions;
      marker: any;
      timeout: NodeJS.Timeout | null;
      constructor(options?: SvgIconOptions);
      setMarker(marker: any): void;
      setStyle(el: JQuery<HTMLElement> | HTMLElement, style: string): void;
      beforeInject(img: HTMLImageElement, svg: SVGElement): void;
      onEnter(): void;
      onLeave(): void;
      onClick(): void;
      _createSvg(svgString: string, container: HTMLElement): void;
      _createImg(src: string): HTMLImageElement;
      _handleEventImage(divMarker: JQuery<HTMLElement>): void;
      createIcon(): HTMLElement;
      _setIconStyles(img: HTMLElement, name: string): void;
    }
    
    function svgIcon(opts: SvgIconOptions): SvgIcon;
  }
}

(L as any).SvgIcon = L.Icon.extend({
  options: {
    className: "leaflet-svg-icon",
    id: "",
    state: "",
    activityState: "",
    iconAnchor: [23, 43],
    network: false,
    decoratorColor: null,
    alertSeverity: null,
    type: "",
    fixedStyle: null,
    containerEl: null,
    selectable: true,
  },

  setMarker: function (marker: L.Marker) {
    this.marker = marker;
  },

  createIcon: function (oldIcon?: HTMLElement) {
    if (oldIcon && oldIcon.tagName === "DIV") {
      return oldIcon;
    }

    const div = document.createElement("div"),
      options = this.options;

    div.id =
      "svg-icon-" + options.id + (options.type ? "-" + options.type : "");

    let url = options.iconUrl;

    if (url != null && url != undefined)
      url = url.includes("?")
        ? url + "&includeMapTemplate=true"
        : url + "?includeMapTemplate=true";

    const img = this._createImg(url);
    div.append(img);

    if (options.bgPos) {
      const bgPos = L.point(options.bgPos);
      div.style.backgroundPosition = -bgPos.x + "px " + -bgPos.y + "px";
    }
    this._setIconStyles(div, "icon");
    return div;
  },

  setStyle: function (el: JQuery<HTMLElement> | HTMLElement, style: string) {
    const bgSelector = '[id^="asset-bg-normal"]';

    if (this.options.fixedStyle) {
      $(el).find(bgSelector).attr("fill", this.options.fixedStyle.bgColor);
      $(el)
        .find('[id^="asset-icon"]')
        .attr("fill", this.options.fixedStyle.iconColor);
      $(el)
        .find('[id^="asset-icon"]')
        .children()
        .attr("fill", this.options.fixedStyle.iconColor);
      return;
    }
    const iconInfo = configurationUtils.getIcon(this.options.state);
    if (!iconInfo) {
      return;
    }

    // Pins get a permanent dark outline (darkest grey token, not #000) in
    // every style state (normal/hover/selected) so they stay visible against
    // the muted basemap — grey offline pins especially.
    $(el)
      .find(bgSelector)
      .attr("stroke", colorDefault.Sensolus_Grey_Darker)
      .attr("stroke-width", "1.5");

    $(el)
      .find('[id^="asset-bg-network"]')
      .attr("visibility", this.options.network ? "visible" : "hidden");
    if (this.options.decoratorColor) {
      $(el)
        .find('[id^="asset-decorator-color"]')
        .attr("fill", this.options.decoratorColor);
    }

    $(el)
      .find('[id^="alert"]')
      .attr("visibility", this.options.alertSeverity ? "visible" : "hidden");

    if (this.options.alertSeverity) {
      const alertbgColor = configurationUtils.getAlertColor(
        this.options.alertSeverity
      );

      $(el).find('[id^="alert-background"]').attr("fill", alertbgColor);
    }

    $(el)
      .find('[id^="motion"]')
      .attr(
        "visibility",
        this.options.activityState === "moving" ? "visible" : "hidden"
      );

    switch (style) {
      case "normal":
      default:
        $(el).find(bgSelector).attr("fill", iconInfo.bgColor || "#333");
        $(el).find('[id^="asset-icon"]').attr("fill", iconInfo.iconColor);
        $(el)
          .find('[id^="asset-icon"]')
          .children()
          .attr("fill", iconInfo.iconColor);
        break;
      case "hover":
        $(el).find(bgSelector).attr("fill", iconInfo.bgHoverColor || "#555");
        $(el).find('[id^="asset-icon"]').attr("fill", iconInfo.iconHoverColor);
        $(el)
          .find('[id^="asset-icon"]')
          .children()
          .attr("fill", iconInfo.iconHoverColor);
        break;
      case "selected":
        $(el).find(bgSelector).attr("fill", (iconInfo as any).bgSelectedColor || "#777");
        $(el).find('[id^="asset-icon"]').attr("fill", iconInfo.iconHoverColor);
        $(el)
          .find('[id^="asset-icon"]')
          .children()
          .attr("fill", iconInfo.iconHoverColor);
        break;
    }
  },

  beforeInject: function (img: HTMLImageElement, svg: SVGElement) {},

  onEnter: function () {
    const self = this;
    self.timeout && clearTimeout(self.timeout);
    self.timeout = setTimeout(function () {
      const key = `#${self.options.containerEl || "map-canvas"}`;
      $("body").find(key).trigger("enter-marker", {
        id: self.options.id,
        type: self.options.type,
        marker: self.marker,
      });
    }, MARKER_HOVER_DELAY_MS);
  },

  onLeave: function () {
    const self = this;

    if (self.timeout != null) {
      clearTimeout(self.timeout);
      self.timeout = null;
    }

    const key = `#${self.options.containerEl || "map-canvas"}`;
    $("body").find(key).trigger("leave-marker", {
      id: self.options.id,
      type: self.options.type,
      marker: self.marker,
    });
  },

  onClick: function () {
    const self = this;

    self.timeout && clearTimeout(self.timeout);
    self.timeout = setTimeout(function () {
      const key = `#${self.options.containerEl || "map-canvas"}`;
      $("body").find(key).trigger("selected-marker", {
        id: self.options.id,
        type: self.options.type,
        marker: self.marker,
      });
    }, 100);
  },

  _createSvg: function (svgString: string, container: HTMLElement) {
    const self = this;
    container.innerHTML = svgString;

    const divMarker = $(container.firstChild as Element).parent();
    if (self.options.state !== "geozone" && self.options.state !== "sitemap") {
      self._handleEventImage(divMarker);
    }
  },

  _createImg: function (src: string): HTMLImageElement {
    const self = this;
    const el = document.createElement("img");
    el.onload = function () {
      SVGInject(el, {
        beforeLoad: function (img: HTMLImageElement) {
          // add a query to circumvent chrome cache (the return value will be used for fetching svg)
          // https://serverfault.com/questions/856904/chrome-s3-cloudfront-no-access-control-allow-origin-header-on-initial-xhr-req
          return img.src + "?q=xhr-2";
        },
        beforeInject: function (img: HTMLImageElement, svg: SVGElement) {
          self.beforeInject(img, svg);
        },
        afterInject: function (img: HTMLImageElement, svg: SVGElement) {
          const divMarker = $(svg).parent();
          if (self.options.state !== "geozone" && self.options.state !== "sitemap") {
            self._handleEventImage(divMarker);
          }
        },
      });
    };
    el.src = src;
    return el;
  },

  _handleEventImage: function (divMarker: JQuery<HTMLElement>) {
    const self = this;
    self.setStyle(divMarker, "normal");

    // Non-selectable markers (e.g. the single-asset detail map) are fully static:
    // no hover style/popup and no click-to-select. Only the normal style is drawn.
    if (self.options.selectable === false) {
      return;
    }

    // hovered marker
    divMarker.on("mouseenter", function () {
      if (!$(this).hasClass("selected")) {
        self.setStyle(this, "hover");
      }
      self.onEnter();
    });
    divMarker.on("mouseleave", function () {
      if (!$(this).hasClass("selected")) {
        self.setStyle(this, "normal");
      }
      self.onLeave();
    });

    // selected marker - skip for geozone icons
    divMarker.on("click", function (event) {
      self.setStyle(this, "selected");
      // deselect previous selected
      const prev = $(this).siblings(".selected");
      if (prev.length) {
        prev.removeClass("selected");
        self.setStyle(prev, "normal");
      }

      if (!$(this).hasClass("selected")) {
        $(this).addClass("selected");
      }
      event.preventDefault();
      self.onClick();
    });

    // programmatic deselect (e.g. when the side panel is closed) - repaint back
    // to the normal style since the selected style set SVG fills directly.
    divMarker.on("deselect-marker", function () {
      if ($(this).hasClass("selected")) {
        $(this).removeClass("selected");
        self.setStyle(this, "normal");
      }
    });
  },

  createShadow: function (): null {
    return null;
  },
});

export const svgIcon = function (opts?: SvgIconOptions) {
  return new (L as any).SvgIcon(opts);
};
export const svgIconExtends = (L as any).SvgIcon;

export const ICON_TYPE = {
  assetType: "asset-type",
  infraType: "infra-type",
};

(L as any).svgIcon = function (opts?: SvgIconOptions): any {
  return new (L as any).SvgIcon(opts);
};